import React, { useState } from 'react'
import _ from 'lodash'

import ViewsLoader from '../../../components/loader/ViewsLoader'
import { ManageClinicSpectators } from './ManageClinicSpectators'
import { ManageEntriesActions } from './ManageEntriesActions'
import { ManageTotals } from './ManageTotals'
import { ManageTotalsActions } from './ManageTotalsActions'
import { ManageEntries } from './ManageEntries'
import EventResults from '../../../components/events/views/details/EventResults'

import { isMinorFn } from '../../../models/interface.helper'

import RiderTeamMemberService from '../../../services/firebase-service/rider-team-member/RiderTeamMemberService'
import RegistrationDyDayService from '../../../services/firebase-service/registration-by-day/RegistrationDyDayService'

import {
  ETabId,
  MANAGE_CLINIC_TABS,
  useManageClinicNOtherAdminTabNew,
} from './hooks/useManageClinicNOtherAdminTabNew'

import { CONST } from '../../../const/const'
import { HORSE_NAME_DEFAULT } from '../../../models/registrations-by-day/registrationByDay.interface'

export type ITotals = {
  entries: { scratched: number; total: number }
  riders: { scratched: number; total: number }
  horses: { scratched: number; total: number }
  registrars: { scratched: number; total: number }
  guardians: { scratched: number; total: number }
  minors: { scratched: number; total: number }
  trainers: { scratched: number; total: number }
  owners: { scratched: number; total: number }
}

interface ManageClinicNOtherAdminTabNewProps {
  eventId: string
}

export const ManageClinicNOtherAdminTabNew: React.FC<ManageClinicNOtherAdminTabNewProps> = ({
  eventId,
}) => {
  const {
    draftRiders,
    tableColumns,
    filteredRiders,
    activeTab,
    setActiveTab,
    loading,
    setActiveStatus,
    allRiders,
    setSearchNameValue,
    activeClass,
    searchNameValue,
    ridersClasses,
    activeStatus,
    setActiveClass,
    syncLoading,
  } = useManageClinicNOtherAdminTabNew(eventId)

  const [totals, setTotals] = useState<ITotals>({
    entries: { scratched: 0, total: 0 },
    riders: { scratched: 0, total: 0 },
    horses: { scratched: 0, total: 0 },
    registrars: { scratched: 0, total: 0 },
    guardians: { scratched: 0, total: 0 },
    minors: { scratched: 0, total: 0 },
    trainers: { scratched: 0, total: 0 },
    owners: { scratched: 0, total: 0 },
  })

  const getTotals = async () => {
    try {
      const riders = await RegistrationDyDayService.getByEventIds([eventId])
      const riderTeamMembers = await RiderTeamMemberService.getByEventId(eventId)

      // Calculate Entries
      totals.entries.total = _(riders).map('id').uniq().size()
      totals.entries.scratched = _(riders).filter({ isScratched: true }).map('id').uniq().size()

      // Calculate Riders
      totals.riders.total = _(riders).map('riderId').uniq().size()
      totals.riders.scratched = _(riders)
        .groupBy('riderId')
        .values()
        .filter((group) => _.every(group, { isScratched: true }))
        .size()

      // Calculate Horses
      totals.horses.total = _(riders)
        .flatMap((r) => r.horsesSelected ?? [])
        .map('id')
        .filter((id) => id !== HORSE_NAME_DEFAULT)
        .uniq()
        .size()
      totals.horses.scratched = _(riders)
        .flatMap((item) =>
          (item.horsesSelected ?? []).map((horse) => ({
            horseId: horse.id,
            isScratched: item.isScratched,
          }))
        )
        .filter((item) => item.horseId !== HORSE_NAME_DEFAULT)
        .groupBy('horseId')
        .values()
        .filter((group) => _.every(group, { isScratched: true }))
        .size()

      // Calculate Registrars
      totals.registrars.total = _(riders).map('userId').uniq().size()
      totals.registrars.scratched = _(riders)
        .groupBy('userId')
        .values()
        .filter((group) => _.every(group, { isScratched: true }))
        .size()

      // Calculate Minors
      totals.minors.total = _(riders)
        .filter((item) => isMinorFn(item.riderDob))
        .map('riderId')
        .uniq()
        .size()
      totals.minors.scratched = _(riders)
        .filter((item) => isMinorFn(item.riderDob))
        .groupBy('riderId')
        .values()
        .filter((group) => _.every(group, { isScratched: true }))
        .size()

      const allScratchedRiderHorseKeys = new Set(
        _(riders)
          .flatMap((r) =>
            (r.horsesSelected ?? []).map((h) => ({
              key: `${r.riderId}|${h.id}`,
              isScratched: !r.isScratched,
            }))
          )
          .filter((item) => {
            const [, horseId] = item.key.split('|')
            return horseId !== HORSE_NAME_DEFAULT
          })
          .groupBy('key')
          .pickBy((group) => _.every(group, { isScratched: true }))
          .keys()
          .value()
      )

      // Calculate Guardians
      totals.guardians.total = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN })
        .map('teamMemberId')
        .uniq()
        .size()
      totals.guardians.scratched = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN })
        .filter((m) => allScratchedRiderHorseKeys.has(`${m.riderId}|${m.horseId}`))
        .map('teamMemberId')
        .uniq()
        .size()

      // Calculate Trainers
      totals.trainers.total = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.TRAINER })
        .map('teamMemberId')
        .uniq()
        .size()
      totals.trainers.scratched = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.TRAINER })
        .filter((m) => allScratchedRiderHorseKeys.has(`${m.riderId}|${m.horseId}`))
        .map('teamMemberId')
        .uniq()
        .size()

      // Calculate Owners
      totals.owners.total = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.OWNER })
        .map('teamMemberId')
        .uniq()
        .size()
      totals.owners.scratched = _(riderTeamMembers)
        .filter({ teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.OWNER })
        .filter((m) => allScratchedRiderHorseKeys.has(`${m.riderId}|${m.horseId}`))
        .map('teamMemberId')
        .uniq()
        .size()

      setTotals({ ...totals })
    } catch (error) {
      console.error('error ', error)
    }
  }

  if (loading) {
    return (
      <div className="w-full min-h-[400px] flex justify-center items-center">
        <ViewsLoader size="lg" color="#F70763" />
      </div>
    )
  }

  return (
    <div className="p-5 rounded-xl bg-SeabiscuitWhiteThemeColor w-full flex flex-col">
      <div className="relative z-20 flex flex-wrap flex-col gap-4 md:flex-row md:items-center justify-between mb-8">
        <div className="flex flex-wrap gap-2 sm:min-w-[300px]">
          {MANAGE_CLINIC_TABS.map((currentItem, index) => {
            return (
              <div
                key={`${currentItem}b${index}`}
                onClick={() => setActiveTab(currentItem.id)}
                className={`py-2 px-4 text-[14px] cursor-pointer mr-2 rounded-xl ${
                  activeTab === currentItem.id
                    ? 'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor'
                    : 'text-SeabiscuitDark200ThemeColor'
                }`}
              >
                {currentItem.tab}
              </div>
            )
          })}
        </div>
        {activeTab === ETabId.entries && (
          <ManageEntriesActions
            activeStatus={activeStatus}
            setActiveStatus={setActiveStatus}
            filteredRiders={filteredRiders}
            allRiders={allRiders}
            setSearchNameValue={setSearchNameValue}
            setActiveClass={setActiveClass}
            activeClass={activeClass}
            searchNameValue={searchNameValue}
            ridersClasses={ridersClasses}
            syncLoading={syncLoading}
            eventId={eventId}
          />
        )}
        {activeTab === ETabId.totals && <ManageTotalsActions getTotals={getTotals} />}
      </div>
      {ETabId.entries === activeTab && (
        <ManageEntries
          draftRiders={draftRiders}
          filteredRiders={filteredRiders}
          activeStatus={activeStatus}
          activeClass={activeClass}
          tableColumns={tableColumns}
        />
      )}
      {ETabId.classes === activeTab && <EventResults isTitleHide hideEntries={false} />}
      {ETabId.spectators === activeTab && <ManageClinicSpectators eventId={eventId} />}
      {ETabId.totals === activeTab && <ManageTotals totals={totals} getTotals={getTotals} />}
    </div>
  )
}
